import { useCallback, useContext, useEffect, useRef, useState, type RefCallback } from 'react';
import { graphql } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';

import { useIsConversationCancelled } from '@atlassian/conversation-assistant-store/controllers/conversation-cancellation';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useSubscriptionInterop } from '@confluence/apollo-relay-interop/entry-points/useSubscriptionInterop.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	useOptimisticCommentAgentSession,
	getOptimisticCommentAgentIdentity,
	getOptimisticCommentAgentSessionMetrics,
	type CommentAgentIdentity,
} from './optimisticCommentAgentSession.ts';
import { useCommentAgentSessionPolling } from './useCommentAgentSessionPolling.ts';
import { AgentSessionPollingContext } from './AgentSessionPollingProvider.tsx';
import { SharedCommentAgentSessionsContext } from './sharedCommentAgentSessions.ts';
import {
	fetchCommentAgentSessionState,
	type pollCommentAgentSessions,
	type PollingFailureReason,
} from './commentAgentSessionPolling.ts';
import {
	getCommentAgentActionKey,
	useSubmittedCommentAgentAction,
} from './submittedCommentAgentAction.ts';
import { AGENT_SESSION_SUBSCRIPTION_FAILED_EVENT } from './agentMentionConstants.ts';
import type { useCommentAgentSessionStatusSubscription as CommentAgentSessionStatusSubscriptionType } from './__generated__/useCommentAgentSessionStatusSubscription.graphql.ts';

/**
 * Live status subscription for a single comment's agent conversation.
 *
 * This is an *observe-only* channel: it streams session-status updates for the given
 * conversation without competing with the conversation's own message stream (owned by the
 * open CwR sidebar right after a mention) — which is what made a direct POST to the chat
 * `message/stream/sse` endpoint unreliable for freshly-mentioned agents.
 *
 * The union member's `__typename` drives our ConversationState:
 *   - ConvoAiTraceMessage                                  → WORKING
 *   - ConvoAiConversationMessage (state COMPLETED)         → hidden
 *   - ConvoAiConversationMessage (state CANCELLED)         → hidden (experiment on)
 *   - ConvoAiConversationMessage (state FAILED)            → FAILED
 *   - ConvoAiConversationMessage (ASSISTANT, has actions)  → INPUT_REQUIRED
 *   - ConvoAiConversationMessage (ASSISTANT, no actions)   → ARTEFACT_READY
 *   - ConvoAiErrorMessage                                  → FAILED
 *   - ConvoAiEmptyConversation / other                     → ignored
 *
 * Modelled on jira-ai/agent-stack-unconnected's AgentSessionStatusSubscription. Defined inline
 * (not a separate module) per graphql-relay-compat/no-import-relay-graphql-operations.
 */
const CommentAgentSessionStatusSubscription = graphql`
	subscription useCommentAgentSessionStatusSubscription($cloudId: ID!, $conversationId: ID!) {
		convoai_onAgentSessionUpdate(cloudId: $cloudId, conversationId: $conversationId)
			@optIn(to: "ConvoAiAgentSessionUpdate") {
			__typename
			... on ConvoAiTraceMessage {
				messageTemplate
			}
			... on ConvoAiConversationMessage {
				role
				agentSessionState
				actions {
					key
				}
			}
			... on ConvoAiErrorMessage {
				errorMessageTemplate: messageTemplate
				statusCode
			}
		}
	}
`;

/**
 * The four conversation states surfaced for a comment's agent session.
 *
 * Modelled after the A2A task state spec:
 * - WORKING        — agent is actively processing
 * - ARTEFACT_READY — agent finished cleanly; no user action required
 * - INPUT_REQUIRED — user must resolve pending actions or a policy block
 * - FAILED         — the agent reported an error
 *
 * See: https://hello.atlassian.net/wiki/spaces/AIC1/pages/7123446755
 */
export type ConversationState = 'WORKING' | 'ARTEFACT_READY' | 'INPUT_REQUIRED' | 'FAILED';

export type CommentAgentSession = {
	/** Attach to the rendered status row; off-screen sessions do not poll. */
	sessionStatusRef?: RefCallback<HTMLDivElement>;
	/**
	 * The derived conversation state, or null when there is no conversation to track yet
	 * (the BE association is not available). FAILED also presents exhausted local status recovery.
	 */
	state: ConversationState | 'THINKING' | null;
	/** Mention identity before CAP creates the session association. */
	agentIdentity?: CommentAgentIdentity;
	/** The conversationId this session is tracking. */
	conversationId: string | undefined;
};

// Snapshot states for which the agent's turn has genuinely ended, so there is nothing left to
// stream — we surface the snapshot directly and skip opening a live subscription (a perf win on
// page load, where most sessions are already settled).
//
// INPUT_REQUIRED is deliberately excluded: it is NOT terminal — it means the agent is paused
// waiting for the user. When the user replies (via the Rovo sidebar) the conversation resumes
// with fresh WORKING → ARTEFACT_READY messages, so the subscription must stay open to receive
// them. Treating it as terminal would freeze the badge on "Waiting for your input" for the
// rest of the session.
const TERMINAL_STATES = new Set<ConversationState>(['ARTEFACT_READY', 'FAILED']);

export const COMPLETION_GRACE_MS = 500;
/** Consecutive unsuccessful background polls before status tracking is unavailable. */
export const MAX_CONSECUTIVE_FAILED_RECOVERY_POLLS = 3;

export type StreamState = ConversationState | 'COMPLETED' | 'CANCELLED';

type SessionUpdateMessage =
	CommentAgentSessionStatusSubscriptionType['response']['convoai_onAgentSessionUpdate'];
type OutcomeObservationSource = 'querySnapshot' | 'subscription' | 'polling';
type SafeErrorMessageTemplate =
	| 'A2A_ERROR'
	| 'ACCEPTABLE_USE_VIOLATIONS'
	| 'ERROR'
	| 'PRE_EXECUTION_ERROR';

function getSafeErrorMessageTemplate(value: unknown): SafeErrorMessageTemplate | undefined {
	switch (value) {
		case 'A2A_ERROR':
		case 'ACCEPTABLE_USE_VIOLATIONS':
		case 'ERROR':
		case 'PRE_EXECUTION_ERROR':
			return value;
		default:
			return undefined;
	}
}

/**
 * Fires an outcome event for an observed agent session state from the query snapshot,
 * subscription, or polling.
 * INPUT_REQUIRED, ARTEFACT_READY and COMPLETED count as success; FAILED counts as failure.
 * WORKING and CANCELLED are ignored. Success does not require the user to post the reply.
 *
 * Only ConvoAiErrorMessage supplies error details; polling provides a state without a message.
 * Callers apply the experiment gate and reject stale or locally cancelled updates.
 * Callers deduplicate success per conversation within the mounted hook; failures are not deduplicated.
 */
function fireCommentAgentSessionOutcomeEvent(
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	state: StreamState,
	observationSource: OutcomeObservationSource,
	message?: SessionUpdateMessage,
	contentType?: string,
	duration?: number,
	isDefaultAgent?: boolean,
) {
	if (state === 'WORKING' || state === 'CANCELLED') return;

	// A response awaiting user input counts as success, even before its action is completed.
	const action = state === 'FAILED' ? 'failed' : 'succeeded';
	const failureMessage =
		action === 'failed' && message?.__typename === 'ConvoAiErrorMessage' ? message : undefined;
	try {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action,
				actionSubject: 'commentAgentSessionReply',
				source: 'agentMention',
				attributes:
					action === 'failed'
						? {
								...(contentType ? { contentType } : {}),
								outcomeState: state,
								observationSource,
								...(duration !== undefined ? { duration } : {}),
								...(isDefaultAgent !== undefined ? { isDefaultAgent } : {}),
								statusCode: failureMessage?.statusCode,
								errorMessageTemplate: getSafeErrorMessageTemplate(
									failureMessage?.errorMessageTemplate,
								),
							}
						: {
								...(contentType ? { contentType } : {}),
								outcomeState: state,
								observationSource,
								...(duration !== undefined ? { duration } : {}),
								...(isDefaultAgent !== undefined ? { isDefaultAgent } : {}),
							},
			},
		}).fire();
	} catch {
		// Analytics must not interrupt the agent session or its status UI.
	}
}

/** Reports subscription transport errors, independently of the agent outcome. */
function fireCommentAgentSessionSubscriptionFailure(createAnalyticsEvent: CreateUIAnalyticsEvent) {
	if (!expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)) return;
	try {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action: 'failed',
				actionSubject: 'commentAgentSessionSubscription',
				source: 'agentMention',
			},
		}).fire();
	} catch {
		// Analytics must not interrupt subscription updates or recovery.
	}
}

/**
 * Reports an unsuccessful status poll or exhausted recovery.
 *
 * @param reason Why the poll could not provide the tracked session's status:
 * - `requestFailed`: a polling request rejected.
 * - `statusMissing`: the response contained no usable status for this conversation.
 * The agent may still be running in either case. Omitted for the `exhausted` action.
 */
function fireCommentAgentSessionPollingAnalytics(
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	action: 'failed' | 'exhausted',
	reason?: PollingFailureReason,
) {
	if (!expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)) return;
	try {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action,
				actionSubject: 'commentAgentSessionPolling',
				source: 'agentMention',
				...(reason ? { attributes: { reason } } : {}),
			},
		}).fire();
	} catch {
		// Analytics must not interrupt polling results or recovery.
	}
}

/**
 * Maps a single `convoai_onAgentSessionUpdate` message to a stream state, or null when the
 * message carries no status signal (and should be ignored). COMPLETED is kept as an internal
 * sentinel so the public state can become null and remove the status row.
 *
 * Only explicit backend failure signals map to FAILED — either a failed agent session state or
 * a ConvoAiErrorMessage. Transport/connection problems are handled by the subscription layer
 * and never surface as a (false) chat failure here.
 */
function mapMessageToState(message: SessionUpdateMessage | null | undefined): StreamState | null {
	switch (message?.__typename) {
		case 'ConvoAiTraceMessage':
			return 'WORKING';
		case 'ConvoAiConversationMessage':
			if (
				message.agentSessionState === 'CANCELLED' &&
				expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)
			) {
				return 'CANCELLED';
			}
			if (message.agentSessionState === 'COMPLETED' || message.agentSessionState === 'FAILED') {
				return message.agentSessionState;
			}
			// Only assistant messages reflect non-terminal agent progress.
			if (message.role !== 'ASSISTANT') {
				return null;
			}
			// Pending actions mean the user has to respond; otherwise the result is ready.
			return message.actions && message.actions.length > 0 ? 'INPUT_REQUIRED' : 'ARTEFACT_READY';
		case 'ConvoAiErrorMessage':
			return 'FAILED';
		default:
			// ConvoAiEmptyConversation and any future/unknown member: no status change.
			return null;
	}
}

export type SessionStateResult = {
	session: Omit<CommentAgentSession, 'sessionStatusRef'>;
	pollingEnabled: boolean;
	onPollRequest: Parameters<typeof pollCommentAgentSessions>[2];
};

/**
 * Subscribes to live status for a single comment's agent conversation and provides
 * its current state and polling configuration.
 *
 * Uses the `convoai_onAgentSessionUpdate` Relay subscription — an observe-only channel that
 * does not compete with the conversation's own message stream (owned by the CwR sidebar),
 * which is what made a direct SSE POST unreliable for freshly-mentioned agents.
 *
 * @param commentId - The comment this session belongs to; keys optimistic startup feedback.
 * @param conversationId - The Rovo conversationId; null until the BE association exists.
 * @param initialState - Optional snapshot state from `content.allCommentAgentSessions`. When
 *                       it is already terminal we surface it directly and skip the subscription.
 * @returns Session state and polling configuration — THINKING during optimistic startup, then CAP status.
 */
export function useCommentAgentSessionState(
	commentId: string,
	conversationId: string | undefined,
	initialState?: ConversationState,
	contentType?: string,
): SessionStateResult {
	const { cloudId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const replyTiming = useRef<{
		commentId: string;
		conversationId: string | undefined;
		startedAt: number | undefined;
		isDefaultAgent: boolean | undefined;
	}>();
	// Capture the invocation time before optimistic discovery clears it. Never time a
	// loaded session from mount: that would understate the latency of its reply.
	useEffect(() => {
		if (expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)) {
			const session = getOptimisticCommentAgentSessionMetrics(commentId);
			const previous =
				replyTiming.current?.commentId === commentId &&
				(!replyTiming.current.conversationId ||
					replyTiming.current.conversationId === conversationId)
					? replyTiming.current
					: undefined;
			replyTiming.current = {
				commentId,
				conversationId,
				startedAt: session?.startedAt ?? previous?.startedAt,
				isDefaultAgent: session?.isDefaultAgent ?? previous?.isDefaultAgent,
			};
		}
	}, [commentId, conversationId]);
	const isLocallyCancelled = useIsConversationCancelled(
		expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)
			? (conversationId ?? '')
			: '',
	);
	const isStarting = useOptimisticCommentAgentSession(
		expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true) ? commentId : '',
		!!conversationId || !!initialState,
	);

	// Deduplicate observations, independently of optimistic discovery. This resets on remount.
	const reportedSuccessConversationIds = useRef(new Set<string>());
	const handleSessionOutcome = useCallback(
		(
			next: StreamState,
			observationSource: OutcomeObservationSource,
			message?: SessionUpdateMessage,
		) => {
			if (next === 'WORKING') return;
			if (next === 'CANCELLED') {
				replyTiming.current = undefined;
				return;
			}
			if (next !== 'FAILED') {
				if (!conversationId || reportedSuccessConversationIds.current.has(conversationId)) return;
				// Mark before firing so repeated subscription/poll updates cannot send success again.
				reportedSuccessConversationIds.current.add(conversationId);
			}
			const startedAt = replyTiming.current?.startedAt;
			if (replyTiming.current) replyTiming.current.startedAt = undefined;
			fireCommentAgentSessionOutcomeEvent(
				createAnalyticsEvent,
				next,
				observationSource,
				message,
				contentType,
				// Milliseconds from local invocation to the first observed reply outcome.
				startedAt !== undefined ? Date.now() - startedAt : undefined,
				replyTiming.current?.isDefaultAgent,
			);
		},
		[conversationId, createAnalyticsEvent, contentType],
	);
	const recovery = useRef<{ failures?: number; exhausted: boolean }>({ exhausted: false });
	const [failedTrackingConversationId, setFailedTrackingConversationId] = useState<string>();
	const previousQuerySnapshot = useRef<{
		conversationId: string | undefined;
		state: ConversationState | undefined;
	}>();

	useEffect(() => {
		const previous = previousQuerySnapshot.current;
		previousQuerySnapshot.current = { conversationId, state: initialState };

		// The page-level query can observe the outcome before the subscription or poll does. Only
		// report a transition for the same conversation; an already-settled initial snapshot may
		// have been loaded during a page refresh and must not be counted as a new response.
		if (
			conversationId &&
			previous?.conversationId === conversationId &&
			previous.state !== initialState &&
			initialState &&
			!isLocallyCancelled &&
			expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)
		) {
			if (!recovery.current.exhausted) {
				if (recovery.current.failures !== undefined) recovery.current.failures = 0;
				handleSessionOutcome(initialState, 'querySnapshot');
			}
		}
	}, [conversationId, initialState, isLocallyCancelled, handleSessionOutcome]);

	// Live status observed from the subscription. null until a message arrives. We never set
	// this from a transport/connection error — only explicit backend failure signals map to
	// FAILED — so a transient blip is never mistaken for a failed chat.
	const [streamState, setStreamState] = useState<StreamState | null>(null);
	const subscriptionRevision = useRef(0);
	const completionTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	const completionPending = useRef(false);
	const pollingScope = useContext(AgentSessionPollingContext);

	const finishRecovery = useCallback(() => {
		// Let an already-pending completion check settle before showing the final error.
		if (
			!expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true) ||
			completionPending.current ||
			recovery.current.exhausted ||
			(recovery.current.failures ?? 0) < MAX_CONSECUTIVE_FAILED_RECOVERY_POLLS
		)
			return;
		recovery.current.exhausted = true;
		setFailedTrackingConversationId(conversationId);
		// The exhaustion guard limits this to one event for the error UI transition.
		fireCommentAgentSessionPollingAnalytics(createAnalyticsEvent, 'exhausted');
	}, [conversationId, createAnalyticsEvent]);

	// Reset observed state when the conversation changes, so a comment never briefly shows a
	// previous conversation's status.
	useEffect(() => {
		recovery.current = { exhausted: false };
		setFailedTrackingConversationId(undefined);
		subscriptionRevision.current += 1;
		clearTimeout(completionTimer.current);
		completionPending.current = false;
		setStreamState(null);
		return () => {
			clearTimeout(completionTimer.current);
			subscriptionRevision.current += 1;
			completionPending.current = false;
		};
	}, [conversationId]);

	// Stopping in Chat is local to the conversation. Drop any prior live status so a later
	// user-initiated turn can resume from fresh subscription data after the cancellation clears.
	useEffect(() => {
		if (isLocallyCancelled) {
			recovery.current = { exhausted: false };
			setFailedTrackingConversationId(undefined);
			replyTiming.current = undefined;
			subscriptionRevision.current += 1;
			clearTimeout(completionTimer.current);
			completionPending.current = false;
			setStreamState(null);
		}
	}, [isLocallyCancelled]);

	const trackingFailed =
		!!conversationId &&
		failedTrackingConversationId === conversationId &&
		expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true);

	// Skip the subscription when there is nothing to observe, or when the backend snapshot is
	// already terminal (the agent finished in a prior session — show the snapshot directly).
	const snapshotIsTerminal = !!initialState && TERMINAL_STATES.has(initialState);
	// A delayed query must not close a subscription that has reported newer work.
	const skip =
		!cloudId ||
		!conversationId ||
		trackingFailed ||
		(snapshotIsTerminal &&
			(!expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true) ||
				(streamState !== 'WORKING' && streamState !== 'INPUT_REQUIRED')));

	function recoverFromSubscriptionFailure() {
		if (
			skip ||
			isLocallyCancelled ||
			recovery.current.exhausted ||
			streamState === 'CANCELLED' ||
			streamState === 'COMPLETED' ||
			(streamState && TERMINAL_STATES.has(streamState)) ||
			!expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)
		)
			return;
		recovery.current.failures ??= 0;
		window.dispatchEvent(new Event(AGENT_SESSION_SUBSCRIPTION_FAILED_EVENT));
		if (completionPending.current) return;
		// Drop stale live status until live updates resume. Preserve a terminal message even
		// when it arrived in the same React batch as the transport completion.
		setStreamState((current) =>
			current === 'CANCELLED' ||
			current === 'COMPLETED' ||
			(current && TERMINAL_STATES.has(current))
				? current
				: null,
		);
	}

	const subscription = useSubscriptionInterop<
		CommentAgentSessionStatusSubscriptionType['response'],
		CommentAgentSessionStatusSubscriptionType['variables']
	>(CommentAgentSessionStatusSubscription, {
		skip,
		variables: { cloudId: cloudId ?? '', conversationId: conversationId ?? '' },
		interopApolloAggClient: true,
		onSubscriptionData: ({ subscriptionData }) => {
			if (
				recovery.current.exhausted &&
				expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)
			)
				return;
			const message = subscriptionData.data?.convoai_onAgentSessionUpdate;
			const next = mapMessageToState(message);
			if (next && !isLocallyCancelled) {
				recovery.current.failures = undefined;
				const revision = ++subscriptionRevision.current;
				clearTimeout(completionTimer.current);
				completionPending.current = false;
				if (
					next === 'COMPLETED' &&
					conversationId &&
					pollingScope &&
					expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)
				) {
					// A tool can complete while the conversation is still working. Allow a subsequent
					// stream update to win before querying the authoritative session state.
					completionPending.current = true;
					completionTimer.current = setTimeout(() => {
						completionTimer.current = undefined;
						void fetchCommentAgentSessionState(
							pollingScope.environment,
							pollingScope.pageId,
							conversationId,
						).then(
							(snapshot) => {
								if (subscriptionRevision.current !== revision) return;
								completionPending.current = false;
								if (snapshot) {
									if (recovery.current.failures !== undefined) recovery.current.failures = 0;
									handleSessionOutcome(snapshot, 'subscription');
									setStreamState(snapshot);
								} else {
									recoverFromSubscriptionFailure();
									finishRecovery();
								}
							},
							() => {
								if (subscriptionRevision.current !== revision) return;
								completionPending.current = false;
								recoverFromSubscriptionFailure();
								finishRecovery();
							},
						);
					}, COMPLETION_GRACE_MS);
				} else {
					if (expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)) {
						handleSessionOutcome(next, 'subscription', message);
					}
					setStreamState(next);
				}
			}
		},
		...(expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)
			? { onSubscriptionComplete: recoverFromSubscriptionFailure }
			: {}),
	});

	useEffect(() => {
		// The subscription hook exposes errors as results, so recovery follows that external update.
		if (subscription?.error) {
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
			recoverFromSubscriptionFailure();
			if (!skip && !isLocallyCancelled)
				fireCommentAgentSessionSubscriptionFailure(createAnalyticsEvent);
		}
		// An unchanged error must not discard live messages received after recovery.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [subscription?.error, conversationId]);

	// Prefer the live subscription status, fall back to the backend snapshot, and finally to
	// WORKING while a conversation exists but has not reported a status yet. A completed session
	// returns null so consumers remove its status row.
	const state: ConversationState | null =
		streamState === 'CANCELLED' || streamState === 'COMPLETED'
			? null
			: (streamState ?? initialState ?? (conversationId ? 'WORKING' : null));

	const onPollRequest = useCallback(() => {
		const revision = subscriptionRevision.current;
		const isRecoveryAttempt = recovery.current.failures !== undefined;
		return (snapshot: Map<string, StreamState>, failureReason?: PollingFailureReason) => {
			if (
				subscriptionRevision.current !== revision ||
				(recovery.current.exhausted &&
					expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true))
			)
				return;
			const next = conversationId ? snapshot.get(conversationId) : undefined;
			if (!next)
				fireCommentAgentSessionPollingAnalytics(
					createAnalyticsEvent,
					'failed',
					failureReason ?? 'statusMissing',
				);
			if (next) {
				if (expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)) {
					if (recovery.current.failures !== undefined) recovery.current.failures = 0;
					handleSessionOutcome(next, 'polling');
					// A valid background snapshot supersedes an older completion confirmation.
					subscriptionRevision.current += 1;
					completionPending.current = false;
					clearTimeout(completionTimer.current);
				}
				setStreamState(next);
			} else if (
				isRecoveryAttempt &&
				recovery.current.failures !== undefined &&
				expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)
			) {
				recovery.current.failures += 1;
				finishRecovery();
			}
		};
	}, [conversationId, handleSessionOutcome, finishRecovery, createAnalyticsEvent]);
	const pollingEnabled =
		!!cloudId &&
		!!conversationId &&
		!isLocallyCancelled &&
		!trackingFailed &&
		(state === 'WORKING' || state === 'INPUT_REQUIRED') &&
		expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true);

	const submission = useSubmittedCommentAgentAction(
		expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)
			? getCommentAgentActionKey(conversationId ?? '', commentId)
			: '',
	);
	// Keep submission progress or recovery visible until comments have been reconciled.
	const hasPendingSubmission = submission && submission.state.status !== 'resolved';
	let displayedState: CommentAgentSession['state'] = state ?? (isStarting ? 'THINKING' : null);
	if (hasPendingSubmission) {
		displayedState = 'INPUT_REQUIRED';
	}
	if (trackingFailed) displayedState = 'FAILED';
	if (isLocallyCancelled || streamState === 'CANCELLED') {
		displayedState = null;
	}
	return {
		session: {
			state: displayedState,
			conversationId,
			agentIdentity: isStarting ? getOptimisticCommentAgentIdentity(commentId) : undefined,
		},
		pollingEnabled,
		onPollRequest,
	};
}

const ignoreSharedSessionPoll: SessionStateResult['onPollRequest'] = () => () => {};

/** Attaches visibility-based polling to a rendered comment's agent session status. */
export function useCommentAgentSession(
	commentId: string,
	conversationId: string | undefined,
	initialState?: ConversationState,
): CommentAgentSession {
	const shared = useContext(SharedCommentAgentSessionsContext);
	// Shared readers must not create another subscription, outcome reporter or recovery handler.
	const local = useCommentAgentSessionState(
		shared ? '' : commentId,
		shared ? undefined : conversationId,
		shared ? undefined : initialState,
	);
	const result = shared
		? (shared.states.get(commentId) ?? {
				session: { state: null, conversationId },
				pollingEnabled: false,
				onPollRequest: ignoreSharedSessionPoll,
			})
		: local;
	const sessionStatusRef = useCommentAgentSessionPolling(
		result.pollingEnabled,
		result.onPollRequest,
	);
	return { ...result.session, sessionStatusRef };
}
